import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["warehouse", "query", "results", "items", "item", "empty", "amount", "difference"]
  static values = {
    documentType: String,
    searchUrl: String,
    emptyText: String,
    notEnoughStockText: String,
    noStockText: String,
    removeText: String,
    removeIcon: String
  }

  connect() {
    this.index = this.itemTargets.length
    this.timeout = null
    this.recalculate()
    this.syncEmptyState()
  }

  search() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.fetchProducts(), 200)
  }

  preventSubmit(event) {
    event.preventDefault()
  }

  clearItems() {
    this.itemTargets.forEach((item) => item.remove())
    this.syncEmptyState()
    this.hideResults()
  }

  removeItem(event) {
    event.currentTarget.closest("tr")?.remove()
    this.recalculate()
    this.syncEmptyState()
  }

  recalculate() {
    this.itemTargets.forEach((item) => {
      const amountTarget = item.querySelector("[data-stock-document-items-target~='amount']")
      const differenceTarget = item.querySelector("[data-stock-document-items-target~='difference']")

      const quantity = this.numberValue(item.querySelector("input[data-role='quantity']")?.value)
      const purchasePrice = this.numberValue(item.querySelector("input[data-role='purchase-price']")?.value)
      if (amountTarget) {
        amountTarget.textContent = this.formatAmount(quantity * purchasePrice)
      }
      if (differenceTarget) {
        const difference = this.differenceValue(item)
        differenceTarget.textContent = this.differenceText(difference)
        this.toggleSurplusPrice(item, difference)
      }
    })
  }

  async fetchProducts() {
    const query = this.queryTarget.value.trim()
    const warehouseId = this.warehouseTarget.value
    if (query.length < 2 || warehouseId.length === 0) {
      this.hideResults()
      return
    }

    const url = new URL(this.searchUrlValue, window.location.origin)
    url.searchParams.set("query", query)
    url.searchParams.set("warehouse_id", warehouseId)
    if (this.documentTypeValue === "inventory") {
      url.searchParams.set("quantity_mode", "physical")
    }

    const response = await fetch(url, { headers: { Accept: "application/json" } })
    if (!response.ok) {
      this.hideResults()
      return
    }

    const data = await response.json()
    if (data.exact_product) {
      this.addProduct(data.exact_product)
      return
    }

    this.renderResults(data.products || [])
  }

  renderResults(products) {
    this.resultsTarget.replaceChildren()
    if (products.length === 0) {
      this.hideResults()
      return
    }

    products.forEach((product) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-primary-50"
      button.innerHTML = `
        <span class="min-w-0">
          <span class="block truncate font-medium text-gray-900">${this.escape(product.title)}</span>
          <span class="block truncate text-xs text-gray-500">${this.escape(product.sku || "")}</span>
        </span>
        <span class="shrink-0 text-xs text-gray-500">${this.escape(String(product.on_hand))}</span>
      `
      button.addEventListener("click", () => this.addProduct(product))
      this.resultsTarget.append(button)
    })

    this.resultsTarget.classList.remove("hidden")
  }

  addProduct(product) {
    if (this.requiresStock && product.on_hand <= 0) {
      window.alert(this.noStockTextValue)
      return
    }

    const existingItem = this.itemTargets.find((item) => item.dataset.productId === String(product.id))
    if (existingItem) {
      this.handleExistingItem(existingItem, product)
    } else {
      this.itemsTarget.append(this.buildItem(product))
    }

    this.queryTarget.value = ""
    this.hideResults()
    this.queryTarget.focus()
    this.recalculate()
    this.syncEmptyState()
  }

  incrementExistingItem(item, product) {
    const input = item.querySelector("input[data-role='quantity']")
    const currentQuantity = this.numberValue(input.value)
    if (this.requiresStock && currentQuantity >= product.on_hand) {
      window.alert(this.notEnoughStockTextValue)
      return
    }

    input.value = String(currentQuantity + 1)
  }

  handleExistingItem(item, product) {
    if (this.documentTypeValue === "inventory") {
      item.querySelector("input[data-role='actual-quantity']")?.focus()
    } else {
      this.incrementExistingItem(item, product)
    }
  }

  buildItem(product) {
    const row = document.createElement("tr")
    row.dataset.stockDocumentItemsTarget = "item"
    row.dataset.productId = product.id
    row.dataset.onHand = product.on_hand

    const index = this.index++
    row.innerHTML = this.rowHtml(product, index)
    return row
  }

  rowHtml(product, index) {
    const cell = "h-10 py-2 text-sm px-3 text-gray-500"
    const centerCell = `${cell} text-center`
    const rightCell = `${cell} text-right`
    const primaryCell = "h-10 py-2 text-sm w-full max-w-0 pl-4 pr-3 sm:w-auto sm:max-w-none sm:pl-6 text-left"
    const actionCell = "h-10 py-1 px-3 text-right text-sm text-gray-500"
    const quantityInput = "block w-20 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-center text-sm leading-normal text-gray-900 transition-colors placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-0"
    const moneyInput = "block w-28 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-right text-sm leading-normal text-gray-900 transition-colors placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-0"

    if (this.documentTypeValue === "receipt") {
      return `
        <td class="${cell} text-left">${this.escape(product.sku || "")}</td>
        <td class="${primaryCell}">
          <span class="font-medium text-gray-900">${this.escape(product.title)}</span>
          <input type="hidden" name="receipt_document[receipt_document_items_attributes][${index}][product_id]" value="${product.id}">
        </td>
        <td class="${centerCell}">${product.on_hand}</td>
        <td class="${centerCell}">
          <input class="${quantityInput}" data-role="quantity" data-action="input->stock-document-items#recalculate" inputmode="numeric" pattern="[0-9]*" name="receipt_document[receipt_document_items_attributes][${index}][quantity]" value="1">
        </td>
        <td class="${rightCell}">
          <input class="${moneyInput}" data-role="purchase-price" data-action="input->stock-document-items#recalculate" inputmode="decimal" pattern="[0-9]+([,.][0-9]{1,2})?" name="receipt_document[receipt_document_items_attributes][${index}][purchase_price]" value="${this.escape(product.purchase_price || "0")}">
        </td>
        <td class="${rightCell}" data-stock-document-items-target="amount"></td>
        <td class="${actionCell}">${this.removeButtonHtml()}</td>
      `
    }

    if (this.documentTypeValue === "inventory") {
      return `
        <td class="${cell} text-left">${this.escape(product.sku || "")}</td>
        <td class="${primaryCell}">
          <span class="font-medium text-gray-900">${this.escape(product.title)}</span>
          <input type="hidden" name="inventory_document[inventory_document_items_attributes][${index}][product_id]" value="${product.id}">
          <input type="hidden" name="inventory_document[inventory_document_items_attributes][${index}][expected_quantity]" value="${product.on_hand}">
        </td>
        <td class="${centerCell}">${product.on_hand}</td>
        <td class="${centerCell}">
          <input class="${quantityInput}" data-role="actual-quantity" data-action="input->stock-document-items#recalculate" inputmode="numeric" pattern="[0-9]*" name="inventory_document[inventory_document_items_attributes][${index}][actual_quantity]" value="${product.on_hand}">
        </td>
        <td class="${centerCell}" data-stock-document-items-target="difference"></td>
        <td class="${rightCell} hidden" data-role="surplus-price-cell">
          <input class="${moneyInput}" data-role="purchase-price" inputmode="decimal" pattern="[0-9]+([,.][0-9]{1,2})?" name="inventory_document[inventory_document_items_attributes][${index}][purchase_price]" value="${this.escape(product.purchase_price || "0")}">
        </td>
        <td class="${actionCell}">${this.removeButtonHtml()}</td>
      `
    }

    const association = this.documentTypeValue === "shipment" ? "shipment_document_items_attributes" : "transfer_document_items_attributes"
    const root = this.documentTypeValue === "shipment" ? "shipment_document" : "transfer_document"

    return `
      <td class="${cell} text-left">${this.escape(product.sku || "")}</td>
      <td class="${primaryCell}">
        <span class="font-medium text-gray-900">${this.escape(product.title)}</span>
        <input type="hidden" name="${root}[${association}][${index}][product_id]" value="${product.id}">
      </td>
      <td class="${centerCell}">${product.on_hand}</td>
      <td class="${centerCell}">
        <input class="${quantityInput}" data-role="quantity" data-action="input->stock-document-items#recalculate" inputmode="numeric" pattern="[0-9]*" name="${root}[${association}][${index}][quantity]" value="1">
      </td>
      <td class="${actionCell}">${this.removeButtonHtml()}</td>
    `
  }

  removeButtonHtml() {
    const label = this.escape(this.removeTextValue)
    const iconUrl = this.escape(this.removeIconValue)

    return `
      <button class="inline-flex size-8 items-center justify-center rounded-md text-red-600 transition-colors hover:bg-red-50 hover:text-red-700" type="button" title="${label}" aria-label="${label}" data-action="stock-document-items#removeItem">
        <span class="size-4 bg-current" style="-webkit-mask: url('${iconUrl}') center / contain no-repeat; mask: url('${iconUrl}') center / contain no-repeat;"></span>
      </button>
    `
  }

  syncEmptyState() {
    if (!this.hasEmptyTarget) return

    this.emptyTarget.classList.toggle("hidden", this.itemTargets.length > 0)
  }

  hideResults() {
    this.resultsTarget.classList.add("hidden")
    this.resultsTarget.replaceChildren()
  }

  get requiresStock() {
    return this.documentTypeValue === "shipment" || this.documentTypeValue === "transfer"
  }

  numberValue(value) {
    const normalized = String(value || "0").replace(",", ".")
    const number = Number.parseFloat(normalized)
    return Number.isFinite(number) ? number : 0
  }

  differenceValue(item) {
    const expected = this.numberValue(item.querySelector("input[name$='[expected_quantity]']")?.value)
    const actual = this.numberValue(item.querySelector("input[data-role='actual-quantity']")?.value)
    return actual - expected
  }

  differenceText(difference) {
    if (difference > 0) return `+${difference}`

    return String(difference)
  }

  toggleSurplusPrice(item, difference) {
    if (this.documentTypeValue !== "inventory") return

    const surplusPriceCell = item.querySelector("[data-role='surplus-price-cell']")
    surplusPriceCell?.classList.toggle("hidden", difference <= 0)
  }

  formatAmount(amount) {
    return amount.toLocaleString("ru-RU", { maximumFractionDigits: 2 })
  }

  escape(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
